import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Divider,
  Flex,
  FormLabel,
  HStack,
  Icon,
  InputGroup,
  Stack,
  Tab,
  Table,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useToast,
} from '@chakra-ui/react';
import { ErrorMessage, FormikProps, setIn } from 'formik';
import * as _ from 'lodash';
import React, { useCallback, useEffect, useState, useMemo } from 'react';

import {
  AdvanceRateFormulaErrors,
  checkReceivableAdvanceRateConfigFormulaErrors,
  prettyPrintAllFormulas,
} from '@finley-technologies/calc-agent';
import { utils as commonUtils } from '@finley-technologies/common';
import { FormulaEngine, LoanProduct } from '@finley-technologies/finley-types';

import { CreditAgreementFullArtifactWipConfigType } from '../../types';
import { CreditAgreement } from '../common';
import { EditButton } from '../edit/EditButton';
import { EditPropertyModal } from '../edit/EditPropertyModal';
import {
  createAdvanceRateNodeShell,
  ReceivableAdvanceRateModifiersTable,
  ReceivableAdvanceRateTreeNodes,
} from './LoanProductReceivableAdvanceRateTree';
import { colors } from 'packages/common-frontend/colors';
import { InputFormField } from 'packages/common-frontend/components/FormField';
import { FormulaContainer } from 'packages/common-frontend/components/FormulaContainer';
import { MultiSelect } from 'packages/common-frontend/components/MultiSelect';
import {
  Select,
  formatSelectOptions,
} from 'packages/common-frontend/components/Select';
import {
  errorToastAdminConfig,
  errorToastId,
} from 'packages/common-frontend/constants';
import { FormulaEditor } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/edit/MonacoEditor/ui/FormulaEditor';
import { FiCheck, FiAlertTriangle } from 'react-icons/fi';
import { useFeatureFlags } from '~/contexts/FeatureFlagProvider';

const headers: (keyof CreditAgreement.DomainEntity.ReceivableAdvanceRateProperty)[] = [
  'name',
  'section',
  '_note_',
];

const propertyKey = 'receivableAdvanceRate';

type FormulaValidationIconProps = {
  errors: AdvanceRateFormulaErrors | null;
};
const FormulaValidationIcon = ({ errors }: FormulaValidationIconProps) => {
  if (_.isNil(errors) || !errors.length) {
    return <Icon as={FiCheck} color="green.300" />;
  }
  return (
    <Tooltip
      label={
        <Stack>
          {errors.map((e: string) => (
            <Text key={e}>Missing Variable: {e}</Text>
          ))}
        </Stack>
      }
      placement="auto-start"
    >
      <Box>
        <Icon as={FiAlertTriangle} color="red.300" />
      </Box>
    </Tooltip>
  );
};

const createFormulaConfigShell = (
  loanProductId: string,
): CreditAgreement.Config.ReceivableAdvanceRate.Formula => ({
  type: 'formula',
  config: {
    formula: '"TODO"',
    engineVersion: FormulaEngine.FullSupportedFormulaEngines.HyperFormula1,
  },
  loanProductIds: [loanProductId],
});

export const createTreeConfigShell = (
  loanProductIds: string[],
): CreditAgreement.Config.ReceivableAdvanceRate.Tree => ({
  type: 'tree',
  root: createAdvanceRateNodeShell({ isRoot: true }),
  modifiers: [],
  loanProductIds,
});

type LoanProductReceivableAdvanceRateProps = {
  rate: CreditAgreement.DomainEntity.ReceivableAdvanceRateProperty;
  basePropertyKey: string;
  idx: number;
  baseFields: CreditAgreement.DataTapeField.BaseConfig[];
  intermediateCalcs: CreditAgreement.Config.LoanTapeField.IntermediateCalcs.IntermediateCalcContainer[];
  concentrationLimitConfigs: CreditAgreement.Config.ConcentrationLimit.SourceConfig[];
  wipConfig?: CreditAgreementFullArtifactWipConfigType;
  loanProductEntity: LoanProduct.LoanProductWithLoanTapeType;
  loanProductOptions: { value: string; label: string }[];
  showReceivableAdvanceRateTreeConfig?: boolean;
};
export const LoanProductReceivableAdvanceRate = ({
  baseFields,
  intermediateCalcs,
  rate,
  basePropertyKey,
  idx,
  wipConfig,
  loanProductEntity,
  loanProductOptions,
  showReceivableAdvanceRateTreeConfig,
  concentrationLimitConfigs,
}: LoanProductReceivableAdvanceRateProps) => {
  const flags = useFeatureFlags();
  const toast = useToast();
  const [isEditOpen, setIsEditOpen] = useState<boolean>(false);
  const [error, setError] = useState<AdvanceRateFormulaErrors>([]);

  const isClientWipMode = wipConfig?.operationMode === 'client';

  const currentLoanProducts = useMemo<{ id: string; name: string }[]>(() => {
    if (!wipConfig) {
      return [];
    }
    const targetLpMechanics = wipConfig.formProps.values.borrowingBaseMechanics.calcAgentMechanics.loanProductMechanics.find(
      (lp) => lp.entity.id === loanProductEntity.id,
    );
    if (!targetLpMechanics) {
      return [];
    }

    return targetLpMechanics.receivableAdvanceRate._config_.receivableAdvanceRateConfig.loanProductIds.map(
      (lpId) => {
        const { id, name } = wipConfig.loanProductsMap[lpId];
        return { id, name };
      },
    );
  }, [loanProductEntity.id, wipConfig]);

  // *** Managing Multi-Select Functionality ***
  const [
    selectedLoanProductOptions,
    updateSelectedLoanProductOptions,
  ] = useState<string[]>(currentLoanProducts.map((lp) => lp.id));

  const [
    loanProductMultiSelectText,
    setLoanProductMultiSelectText,
  ] = useState<string>(currentLoanProducts.map((lp) => lp.name).join(', '));
  const defaultLoanProductMultiSelectText = 'Select loan products...';

  const multiSelectOnChange = useCallback(
    (targetId: string): string[] => {
      if (!wipConfig) {
        return [];
      }

      // Manage updating the selected options
      let newLoanProductOptions: string[] = [];
      const isExistingOption = selectedLoanProductOptions.includes(targetId);
      if (isExistingOption) {
        newLoanProductOptions = selectedLoanProductOptions.filter(
          (id) => id !== targetId,
        );
      } else {
        // Using "loanProductOptions" ensures same name ordering as LP mechanics
        for (const { value } of loanProductOptions) {
          if (
            value === targetId ||
            selectedLoanProductOptions.includes(value)
          ) {
            newLoanProductOptions.push(value);
          }
        }
      }
      updateSelectedLoanProductOptions(newLoanProductOptions);

      // Manage text display of the multi-select
      let selectText = defaultLoanProductMultiSelectText;
      if (newLoanProductOptions.length > 0) {
        const newSelectedLabels = newLoanProductOptions.map<string>(
          (lpId) => wipConfig.loanProductsMap[lpId].name,
        );
        selectText = newSelectedLabels.join(', ');
      }
      setLoanProductMultiSelectText(selectText);

      return newLoanProductOptions;
    },
    [loanProductOptions, selectedLoanProductOptions, wipConfig],
  );

  const fullPropertyKey = `${basePropertyKey}[${idx}].${propertyKey}`;
  useEffect(() => {
    const checkError = checkReceivableAdvanceRateConfigFormulaErrors({
      receivableAdvanceRateConfig: rate._config_.receivableAdvanceRateConfig,
      baseFields,
      intermediateCalcs: intermediateCalcs as CreditAgreement.Config.LoanTapeField.IntermediateCalcs.IntermediateCalcContainer[],
      concentrationLimitConfigs,
    });

    if (checkError && checkError.length && !toast.isActive(errorToastId)) {
      const description = (
        <Text>
          Receivable advance rate is missing variables:
          {JSON.stringify(checkError)}
        </Text>
      );
      errorToastAdminConfig.title = `There Are Errors In The Config Formula`;
      toast({ ...errorToastAdminConfig, description });
    }
    if (checkError.length) {
      setError((previousError) => {
        if (!_.isEqual(checkError, previousError)) {
          return checkError;
        }
        return previousError;
      });
    } else {
      setError([]);
    }
  }, [baseFields, intermediateCalcs, rate, toast, concentrationLimitConfigs]);

  const validate = useCallback(
    (
      values: CreditAgreement.DomainEntity.ReceivableAdvanceRateProperty,
    ): AdvanceRateFormulaErrors => {
      // Config Errors
      const configErrors = checkReceivableAdvanceRateConfigFormulaErrors({
        receivableAdvanceRateConfig:
          values._config_.receivableAdvanceRateConfig,
        baseFields,
        intermediateCalcs,
        concentrationLimitConfigs,
      });

      // Loan Product Multi-Select Errors
      const multiLoanProductSelectErrors: string[] = [];
      if (selectedLoanProductOptions.length === 0) {
        multiLoanProductSelectErrors.push('No loan products were selected.');
      } else if (!selectedLoanProductOptions.includes(loanProductEntity.id)) {
        multiLoanProductSelectErrors.push(
          `Need to include own loan product [${loanProductEntity.name}].`,
        );
      }

      return [...configErrors, ...multiLoanProductSelectErrors];
    },
    [
      baseFields,
      intermediateCalcs,
      loanProductEntity,
      selectedLoanProductOptions,
      concentrationLimitConfigs,
    ],
  );

  const engineTypeOptions = useMemo<
    {
      value:
        | FormulaEngine.SupportedFormulaEngines
        | FormulaEngine.FullSupportedFormulaEngines;
      label: string;
    }[]
  >(() => {
    return flags.isExcelEngineAdvanceRateEnabled
      ? formatSelectOptions(FormulaEngine.FullSupportedFormulaEngines)
      : formatSelectOptions(FormulaEngine.SupportedFormulaEngines);
  }, [flags.isExcelEngineAdvanceRateEnabled]);

  const isFormulaReceivableAdvanceRateConfigType =
    rate._config_.receivableAdvanceRateConfig.type === 'formula';

  const configPrefix = '_config_';
  const receivableAdvanceRateConfigPrefix = 'receivableAdvanceRateConfig';
  const fieldValuePrefix = `${configPrefix}.${receivableAdvanceRateConfigPrefix}`;

  const ConditionalPropertiesComponent = (
    localFormProps: FormikProps<any>,
    validationErrors: Array<string>,
  ) => {
    return (
      <Box>
        {!_.isEmpty(validationErrors) && (
          <Alert status="error" my="3" rounded="md">
            <AlertIcon />
            <AlertDescription>
              Errors: {validationErrors.join(',')}
            </AlertDescription>
          </Alert>
        )}

        {/*
         * *** Multi-LP Select Type ***
         * @MLP Advance Rate: clean up this section once
         * we have proper support for MLP advance rate
         */}
        <Box w="100%" mb="24px">
          <FormLabel mb="8px">Loan Products</FormLabel>
          <InputGroup width="100%">
            <MultiSelect
              options={loanProductOptions}
              selectedOptions={selectedLoanProductOptions}
              onChange={(value: string) => {
                const selectedLoanProductIds = multiSelectOnChange(value);
                localFormProps.setFieldValue(
                  `${fieldValuePrefix}.loanProductIds`,
                  selectedLoanProductIds,
                  true,
                );
                localFormProps.setFieldTouched(
                  `${fieldValuePrefix}.loanProductIds`,
                  true,
                );
              }}
              titleText={loanProductMultiSelectText}
            />
          </InputGroup>
          <ErrorMessage name="loanProductIds">
            {(msg) => (
              <Text color={colors.error} mt="5px">
                {msg}
              </Text>
            )}
          </ErrorMessage>
        </Box>

        {/* *** Implementation Editor *** */}
        {localFormProps.values[configPrefix][receivableAdvanceRateConfigPrefix]
          .type === 'formula' && (
          <Box w="100%">
            {!isClientWipMode && (
              <Box mb="24px">
                <FormLabel w="60" mb="8px">
                  Formula Engine
                </FormLabel>
                <InputGroup width="100%">
                  <Select
                    value={
                      localFormProps.values[configPrefix][
                        receivableAdvanceRateConfigPrefix
                      ].config.engineVersion
                    }
                    options={engineTypeOptions}
                    onChange={(engineVersion) => {
                      const oldConfig: CreditAgreement.Config.ReceivableAdvanceRate.Formula =
                        localFormProps.values[configPrefix][
                          receivableAdvanceRateConfigPrefix
                        ].config;
                      const newConfig = {
                        ...oldConfig,
                        engineVersion,
                      };
                      localFormProps.setFieldValue(
                        `${fieldValuePrefix}.config`,
                        newConfig,
                      );
                    }}
                    isDisabled={!flags.isExcelEngineAdvanceRateEnabled}
                  />
                </InputGroup>
              </Box>
            )}

            <FormLabel mb="8px">Formula</FormLabel>
            <Box
              border="1px"
              borderColor={colors.gray[200]}
              borderRadius="8px"
              p="8px"
            >
              <FormulaEditor
                value={
                  localFormProps.values[configPrefix][
                    receivableAdvanceRateConfigPrefix
                  ].config.formula
                }
                onChange={(value) => {
                  localFormProps.setFieldValue(
                    `${fieldValuePrefix}.config.formula`,
                    value,
                  );
                }}
                operationMode={wipConfig?.operationMode}
                language="formula-receivableAdvanceRate"
              />
            </Box>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <>
      <Divider width="full" />
      <Text fontSize="20px" fontWeight="bold" mt={4} mb={4}>
        Receivable Advance Rate
      </Text>
      {(commonUtils.EnvUtils.isDevelopment() ||
        showReceivableAdvanceRateTreeConfig) && (
        <HStack mb="10px">
          <Text fontSize="16px" fontWeight="bold" mr={2}>
            Config Type
          </Text>
          {wipConfig?.isMutateAllowed(fullPropertyKey) && (
            <Box width="xs">
              <Select
                value={rate._config_.receivableAdvanceRateConfig.type}
                options={formatSelectOptions(
                  CreditAgreement.Config.ReceivableAdvanceRate.Types,
                  'title',
                )}
                onChange={(
                  type:
                    | CreditAgreement.Config.ReceivableAdvanceRate.MethodType
                    | undefined,
                ) => {
                  let newConfig:
                    | CreditAgreement.Config.ReceivableAdvanceRate.Config
                    | undefined;
                  switch (type) {
                    case 'formula':
                      newConfig = createFormulaConfigShell(
                        loanProductEntity.id,
                      );
                      break;
                    case 'tree':
                      newConfig = createTreeConfigShell(
                        loanProductOptions.map((lp) => lp.value),
                      );
                      break;
                    default:
                      break;
                  }

                  if (newConfig) {
                    const {
                      formProps,
                      saveCreditAgreementFullArtifactWip,
                    } = wipConfig;

                    formProps.setFieldValue(
                      `${fullPropertyKey}._config_.receivableAdvanceRateConfig`,
                      newConfig,
                    );
                    const updatedWipCreditAgreement = setIn(
                      formProps.values,
                      `${fullPropertyKey}._config_.receivableAdvanceRateConfig`,
                      newConfig,
                    );
                    saveCreditAgreementFullArtifactWip({
                      variables: {
                        creditAgreementFullArtifactWip: {
                          id: updatedWipCreditAgreement.id,
                          creditFacilityId:
                            updatedWipCreditAgreement.creditFacilityId,
                          creditAgreement: JSON.stringify(
                            updatedWipCreditAgreement,
                          ),
                        },
                      },
                    });
                  }
                }}
              />
            </Box>
          )}
        </HStack>
      )}
      <Box display="flex" mb="35px" width="100%">
        {rate._config_.receivableAdvanceRateConfig.type === 'formula' && (
          <Flex width="100%" overflowY="auto">
            <Table borderWidth="1px">
              <Thead>
                <Tr>
                  {headers.map((header) => (
                    <Th
                      key={header}
                      minWidth={header === 'name' ? '15rem' : '10rem'}
                    >
                      {_.startCase(header)}
                    </Th>
                  ))}
                  {isFormulaReceivableAdvanceRateConfigType && (
                    <>
                      <Th width="6rem" minWidth="6rem">
                        Valid
                      </Th>
                      <Th width="25rem" minWidth="25rem">
                        Formula
                      </Th>
                    </>
                  )}
                  {wipConfig && <Th width="8.5rem" minWidth="8.5rem" />}
                </Tr>
              </Thead>
              <Tbody>
                <Tr>
                  {headers.map((header) => {
                    return (
                      <Td key={`${fullPropertyKey}.${String(header)}`}>
                        {/* !TODO @eldarshamukhamedov `rate[header]` lookup can return a
                        `{ receivableValuationRateConfig: Config; }` object, which isn't a string. It's
                        not clear how it's meant to be rendered, but this is almost certainly a bug. */}
                        {/* @ts-expect-error @eldarshamukhamedov Implicit cast of object to string */}
                        {rate[header] ? rate[header] : '-'}
                      </Td>
                    );
                  })}
                  {rate._config_.receivableAdvanceRateConfig.type ===
                    'formula' && (
                    <>
                      <Td>
                        <FormulaValidationIcon errors={error} />
                      </Td>
                      <Td whiteSpace="pre-wrap">
                        <FormulaContainer>
                          {
                            rate._config_.receivableAdvanceRateConfig.config
                              .formula
                          }
                        </FormulaContainer>
                      </Td>
                    </>
                  )}
                  {wipConfig?.isMutateAllowed(fullPropertyKey) && (
                    <Td>
                      <EditButton setIsOpen={setIsEditOpen} />
                      <EditPropertyModal<CreditAgreement.DomainEntity.ReceivableAdvanceRateProperty>
                        isOpen={isEditOpen}
                        setIsOpen={setIsEditOpen}
                        wipConfig={wipConfig}
                        basePropertyKey={`${fullPropertyKey}`}
                        conditionalProperties={{
                          conditionalPropertiesComponent: ConditionalPropertiesComponent,
                          conditionalPropertiesToggleLabel: 'Configuration',
                          conditionalPropertiesValidation: validate,
                          conditionalPropertiesOnSubmitTransform: (values) =>
                            prettyPrintAllFormulas(values),
                          useConditionalProperties: !!rate._config_
                            .receivableAdvanceRateConfig,
                        }}
                      >
                        {(localFormProps) => (
                          <InputFormField
                            propName="value"
                            label="Value"
                            formProps={localFormProps}
                            value={localFormProps.values.value}
                            placeholder="Enter value"
                          />
                        )}
                      </EditPropertyModal>
                    </Td>
                  )}
                </Tr>
              </Tbody>
            </Table>
          </Flex>
        )}
        {rate._config_.receivableAdvanceRateConfig.type === 'tree' && (
          <Stack width="full">
            <Tabs>
              <TabList>
                <Tab>Advance Rate</Tab>
                <Tab>Modifiers</Tab>
              </TabList>

              <TabPanels>
                <TabPanel>
                  <ReceivableAdvanceRateTreeNodes
                    advanceRateTreeConfig={
                      rate._config_.receivableAdvanceRateConfig
                    }
                    wipConfig={wipConfig}
                    fullPropertyKey={`${fullPropertyKey}._config_.receivableAdvanceRateConfig.root`}
                    depth={0}
                    modifierNames={rate._config_.receivableAdvanceRateConfig.modifiers.map(
                      (m) => m.name,
                    )}
                    baseFields={baseFields}
                    intermediateCalcs={intermediateCalcs}
                    concentrationLimitConfigs={concentrationLimitConfigs}
                  />
                </TabPanel>
                <TabPanel>
                  <ReceivableAdvanceRateModifiersTable
                    advanceRateTreeConfig={
                      rate._config_.receivableAdvanceRateConfig
                    }
                    fullPropertyKey={`${fullPropertyKey}._config_.receivableAdvanceRateConfig.modifiers`}
                    wipConfig={wipConfig}
                    baseFields={baseFields}
                    intermediateCalcs={intermediateCalcs}
                    concentrationLimitConfigs={concentrationLimitConfigs}
                  />
                </TabPanel>
              </TabPanels>
            </Tabs>
          </Stack>
        )}
      </Box>
      <Divider width="full" marginBottom={4} />
    </>
  );
};
